import React, { Fragment, useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
	IVignetteOffer,
	OfferContext,
	getVignetteByCatalogAndFuel,
	getDecimalPriceAmountByCatalogId,
	VignetteContext,
	getCheapestTollSection,
	calculateNewDates,
	convertApiDate,
	IDates,
	ITypeVignetteOrderingProcessFields,
	getLabelValue,
	getUrlSearchParamsAsStack,
	preSelectFirstMatchingVignette,
} from '@adac-mp/shared';
import {
	MultiSelection,
	ProductOptionLabel,
	RadioButtons,
	SkeletonTemplate,
	Typography,
	renderErrorMessage,
} from '../../index';
import { useFlowId } from '../../hooks/useFlowId';
interface IVignetteRadioButtonProps {
	defaultVignetteId?: string;
	hasError?: boolean;
	defaultCategoryId?: number;
	moduleTypeIdFilter?: number;
	selectedFuelId: number;
	supplierId?: number;
	teaserFuelId?: number;
	configure?: boolean;
	dates?: IDates | undefined;
	hasFuelError?: boolean;
	onVignetteChanged: (vignette: IVignetteOffer, multiSelect?: boolean) => void;
	updateValidatedStepsMap?: (hasError: boolean, reset?: boolean) => void;
}

type ModuleIsSelected = {
	id: number;
	checked: boolean; // checkbox is checked
	selected: boolean; // radio button is selected
};
export const VignetteRadioButtons = ({
	hasError,
	selectedFuelId = -1,
	defaultVignetteId = undefined,
	defaultCategoryId = undefined,
	moduleTypeIdFilter = undefined,
	teaserFuelId = undefined,
	supplierId = undefined,
	configure = false,
	hasFuelError = false,
	dates,
	onVignetteChanged,
	updateValidatedStepsMap = () => {},
}: IVignetteRadioButtonProps) => {
	const flowId = useFlowId();
	const [searchParams] = useSearchParams();
	const { vignetteOffers, tollSectionOffers, labels } = useContext(OfferContext);
	const { vehicleCategory, selectedProducts, contentfulLabels } = useContext(VignetteContext);
	const [availableVignettes, setAvailableVignettes] = useState<IVignetteOffer[][] | undefined>(undefined);
	const [vignetteId, setVignetteId] = useState<string | undefined>(defaultVignetteId);
	const [moduleIsSelected, setModuleSelected] = useState<ModuleIsSelected[]>([]);
	const currentLabels = contentfulLabels as ITypeVignetteOrderingProcessFields;
	const hasMultiSelect = supplierId === 3 && flowId === 0 && configure;
	const [preSelectedVignetteIds, setPreSelectedVignetteIds] = useState<string[]>([]);
	const preFilledSectionTolls = getUrlSearchParamsAsStack('section-tolls', searchParams);
	const flexTollSection = tollSectionOffers.find(({ moduleTypeId }) => moduleTypeId === 4);
	const prefilledFlex = preFilledSectionTolls.includes(flexTollSection?.matchCode);
	const hasPrefilledSectionTolls = preFilledSectionTolls.length > 0 && !prefilledFlex;

	// map transporter to PKW for Austria
	const moduleDetailMapAustria = {
		3: 3,
		4: 2,
		2: 2,
	};

	const updateAvailableVignettes = () => {
		const tollSection = getCheapestTollSection(tollSectionOffers);

		const vignetteSections = [
			[
				...vignetteOffers
					.filter((vignette) =>
						moduleTypeIdFilter ? vignette.moduleTypeId === moduleTypeIdFilter : vignette,
					)
					.filter((vignette) => {
						const currentVignetteCategoryId = defaultCategoryId ?? vehicleCategory?.id;
						if (currentVignetteCategoryId && vignette.supplierId !== 6) {
							return vignette?.supplierId === 3
								? moduleDetailMapAustria[currentVignetteCategoryId] === vignette.moduleDetailId
								: currentVignetteCategoryId === vignette.moduleDetailId;
						}
						return vignette;
					})
					.filter(({ fuelTypeId }) => fuelTypeId === selectedFuelId)
					.filter(({ supplierId: currSupplierId }) => currSupplierId === supplierId)
					.reduce(
						(itemsMap, item) =>
							itemsMap.has(item.serviceCatalogName)
								? itemsMap
								: itemsMap.set(item.serviceCatalogName, item),
						new Map(),
					)
					.values(),
			]
				.sort(
					(a, b) =>
						getDecimalPriceAmountByCatalogId(a.priceComponents) -
						getDecimalPriceAmountByCatalogId(b.priceComponents),
				)
				.flat(),
		];

		if (tollSection && moduleTypeIdFilter !== 2 && supplierId === 3) {
			vignetteSections.push(tollSection);
		}

		setAvailableVignettes(vignetteSections);
	};

	useEffect(() => {
		if (vignetteOffers.length > 0) {
			updateAvailableVignettes();
		}
	}, [vignetteOffers, tollSectionOffers]);

	useEffect(() => {
		if (availableVignettes) {
			const urlPreSelectedVignettes = getUrlSearchParamsAsStack('vignettes', searchParams);
			const preSelectedVignettes = preSelectFirstMatchingVignette(
				availableVignettes[0],
				urlPreSelectedVignettes,
				vehicleCategory,
			);

			preSelectedVignettes.forEach((preSelectedVignette) => {
				if (preSelectedVignette) {
					setVignetteId(preSelectedVignette.serviceCatalogCode);
					onVignetteChanged(preSelectedVignette);
					setPreSelectedVignetteIds([...preSelectedVignetteIds, preSelectedVignette.serviceCatalogCode]);
				}
			});

			const hasPrefilledVignettes = Boolean(preSelectedVignettes.length > 0);
			setModuleSelected(
				availableVignettes.map((_, index) => {
					if (index === 0 && hasPrefilledVignettes) {
						return { id: index, checked: true, selected: true };
					}
					if (index === 1 && (hasPrefilledSectionTolls || prefilledFlex)) {
						return { id: index, checked: true, selected: true };
					}
					return { id: index, checked: false, selected: false };
				}),
			);

			if (supplierId === 14 && availableVignettes.length === 1 && availableVignettes[0].length === 1) {
				const selectableVignette = availableVignettes[0][0];
				setVignetteId(selectableVignette.serviceCatalogCode);
				onVignetteChanged(selectableVignette);
			}
		}
	}, [availableVignettes]);

	// updateValidatedStepsMap logic
	useEffect(() => {
		if (hasMultiSelect) {
			const isNotValid =
				moduleIsSelected.every((module) => module.checked === false) ||
				moduleIsSelected.some((module) => module.checked === true && module.selected === false);
			updateValidatedStepsMap(!isNotValid);
		} else {
			const isValid = selectedProducts.some(({ supplierId: currSupplierId }) => currSupplierId === supplierId);
			updateValidatedStepsMap(isValid);
		}
	}, [selectedProducts, moduleIsSelected]);

	useEffect(() => {
		updateAvailableVignettes();
		if (vignetteId && supplierId === 6) {
			const currentVignette = getVignetteByCatalogAndFuel(vignetteOffers, vignetteId, selectedFuelId);
			if (currentVignette) {
				onVignetteChanged(currentVignette);
			}
		}
	}, [selectedFuelId]);

	const moduleAndProductIsSelectedById = (module: number, checked: boolean, selected: boolean) => {
		setModuleSelected((prev) => prev.map((item) => (item.id === module ? { ...item, checked, selected } : item)));
	};

	const isModulePreSelected = (moduleTypeId: number) =>
		moduleTypeId === 2 ? moduleIsSelected[0]?.checked : moduleIsSelected[1]?.checked;

	const getDefaulVignetteId = (moduleTypeId: number) => {
		if (moduleTypeId === 2) {
			return preSelectedVignetteIds[0];
		}
		if (hasPrefilledSectionTolls) {
			const tollSection = getCheapestTollSection(tollSectionOffers);
			if (tollSection) {
				return tollSection[0].serviceCatalogCode;
			}
		}
		if (prefilledFlex) {
			return flexTollSection?.matchCode;
		}
		return undefined;
	};

	return (
		<>
			{hasError &&
				(!moduleIsSelected.some((module) => module.checked === true && module.selected === false) ||
					hasFuelError) &&
				renderErrorMessage({
					message: hasFuelError
						? 'Bitte wählen Sie die Kraftstoffart Ihres Fahrzeugs aus.'
						: currentLabels?.errorProductSelection || 'Bitte wählen Sie einen Artikel aus',
					className: 'mb-5',
				})}
			<div>
				{availableVignettes && moduleIsSelected.length > 0 ? (
					availableVignettes.map((vignettes, index) => {
						return vignettes.length > 0 ? (
							<Fragment key={index}>
								{vignettes[0].supplierId === 3 && moduleTypeIdFilter !== 2 && (
									<Typography className=" inline-flex pl-4 my-3 first:mt-0" color="title">
										{getLabelValue('mp.digital', labels) || 'Digitale'}{' '}
										{vignettes[0].moduleTypeId === 2
											? vignettes[0].moduleType
											: getLabelValue('mp.tollSection', labels)}
									</Typography>
								)}
								{hasMultiSelect ? (
									<MultiSelection
										index={index}
										hasError={hasError}
										products={vignettes}
										supplierId={vignettes[0].supplierId}
										configure={configure}
										defaultVignetteId={getDefaulVignetteId(vignettes[0].moduleTypeId)}
										defaultModuleValue={isModulePreSelected(vignettes[0].moduleTypeId)}
										moduleAndProductIsSelectedById={moduleAndProductIsSelectedById}
										onVignetteChanged={onVignetteChanged}
									/>
								) : (
									<div className="rounded bg-white">
										<RadioButtons
											key={`${vignettes[0].moduleType} ${index}`}
											className={`${
												configure && 'px-4'
											} py-2 border-b border-l-0 border-t-0 border-r-0 border-solid border-b-gray-600 last:border-b-0 last:rounded-b md:py-3`}
											labelClassName="w-full"
											value={String(vignetteId)}
											setValue={(option) => {
												const selectedVignetteId = option;
												const currentDates = selectedProducts[0]?.dates || dates;
												const currentOffers =
													vignettes[0].moduleTypeId === 3 || vignettes[0].moduleTypeId === 4
														? vignettes
														: vignetteOffers;
												const currentVignette = currentOffers.find(
													({ serviceCatalogCode, usageFrom, usageTo }) =>
														serviceCatalogCode === selectedVignetteId &&
														(!currentDates ||
															(currentDates &&
																currentDates.start.getTime() >=
																	convertApiDate(usageFrom).getTime() &&
																currentDates.start.getTime() <=
																	convertApiDate(usageTo).getTime())),
												);
												setVignetteId(selectedVignetteId);
												if (currentVignette) {
													if (selectedProducts[0]?.dates) {
														const prevStartDate = selectedProducts[0]?.dates?.start;
														const newDates = calculateNewDates(
															prevStartDate,
															currentVignette,
														);
														onVignetteChanged({
															...currentVignette,
															dates: newDates,
															plates: selectedProducts[0]?.plates,
														});
													} else if (currentDates) {
														const newDates = calculateNewDates(
															currentDates?.start,
															currentVignette,
														);
														onVignetteChanged({ ...currentVignette, dates: newDates });
													} else {
														onVignetteChanged(currentVignette);
													}
												}
											}}
											size="md"
											options={vignettes.map(
												({
													serviceCatalogCode,
													serviceCatalogName,
													priceComponents,
													moduleTypeId,
													moduleType,
													downtimes,
												}) => ({
													id: serviceCatalogCode,
													label: (
														<ProductOptionLabel
															supplierId={supplierId}
															catalogId={serviceCatalogCode}
															serviceCatalogName={
																moduleTypeId === 3 ? moduleType : serviceCatalogName
															}
															priceComponents={priceComponents}
															moduleTypeId={moduleTypeId}
															downtimes={downtimes}
															selectedFuelId={selectedFuelId}
															teaserFuelId={teaserFuelId}
														/>
													),
													title: serviceCatalogName,
												}),
											)}
										/>
									</div>
								)}
							</Fragment>
						) : null;
					})
				) : (
					<SkeletonTemplate />
				)}
			</div>
		</>
	);
};
